import React, { FC } from 'react'
import camelCase from 'lodash/camelCase'
import moment from 'moment'
import ControlledHiddenInput from 'transfix-form/components/ControlledHiddenInput'
import DropTrailerIcon from 'transfix-icons/build/DropTrailerIcon'
import { MapMarker } from 'transfix-ui/components/MapMarker'
import { Box } from 'transfix-ui/core'
import { STOP_TYPE } from 'types'
import { ShipmentStop } from 'apps/carrier/apiTypes/activeShipments'
import { Tag, TAG_SIZE, TAG_STYLE } from 'transfix-ui'
import { capitalize } from 'utils/capitalize'
import { FORMATS } from 'constants/datetime'
import ControlledDateTimeFields from './ControlledDateTimeFields'
import { StopType } from './DateTimeFields'
import LateReasonDropdownRender from './LateReasonDropdownRender'

export interface IStopProps {
  type: STOP_TYPE
  stop: ShipmentStop
  isLastStop?: boolean
  isEtaRequired?: boolean
}

interface IMissingStopFields {
  manualEta?: boolean
  arrivedAt?: boolean
  leftAt?: boolean
  trailerEmptiedAt?: boolean
}

enum APPOINTMENT_STATUS {
  confirmed = 'confirmed',
  unconfirmed = 'unconfirmed',
  rescheduled = 'rescheduled',
}

const timeRangeLabel = (start?: string, end?: string, timezone: string = '') =>
  end
    ? `${moment(start).format(FORMATS.timeFormat)} - ${moment
        .tz(end, timezone)
        .format(FORMATS.timeFormat)}`
    : moment.tz(start, timezone).format(FORMATS.timeFormat)

const tagStyleTypes: {
  [key: string]: TAG_STYLE
} = {
  [APPOINTMENT_STATUS.confirmed]: TAG_STYLE.success,
  [APPOINTMENT_STATUS.unconfirmed]: TAG_STYLE.warning,
  [APPOINTMENT_STATUS.rescheduled]: TAG_STYLE.error,
}

const StopFields: FC<IStopProps> = ({ isEtaRequired, isLastStop, stop, type }) => {
  // Transforms array of missing stop fields to an object set to 'true' value
  // in order to highlight its corresponding input
  const missingFields = stop.missingFields?.reduce((acc: IMissingStopFields, curr) => {
    acc[camelCase(curr) as keyof IMissingStopFields] = true
    return acc
  }, {})

  const stopIdx = (stop.stopSequence || 1) - 1
  const stopTypeKey = type === 'pickup' ? 'pickupsAttributes' : 'deliveriesAttributes'
  const stopTypeField = `${stopTypeKey}[${stopIdx}]`
  const stopFieldType = {
    index: stopIdx,
    type: stopTypeKey as StopType,
  }
  const formattedStartDate = moment(stop.appointment?.startAt).format(FORMATS.dayFormat)
  const appointmentTimeLabel = timeRangeLabel(
    stop.appointment?.startAt,
    stop.appointment?.endAt,
    stop.appointment?.timezone
  )
  const appointmentStatus = stop.hasRescheduleRequest
    ? APPOINTMENT_STATUS.rescheduled
    : stop.appointment?.status || APPOINTMENT_STATUS.unconfirmed
  const labelStyle = tagStyleTypes[appointmentStatus]

  const isStopCompleted = Boolean(stop.arrivedAt && stop.leftAt)

  return (
    <Box
      display='flex'
      flexWrap='wrap'
      width={1}
      alignItems='top'
      className='stopfields'
      role='row'>
      <Box display='flex' alignItems='top' minWidth={225}>
        <Box display='flex' alignItems='center'>
          <MapMarker sequence={stop.stopSequence || 1} type={type} />
        </Box>
        <Box display='flex' flexDirection='column' justifyContent='center' ml={1}>
          <Box display='flex' alignItems='center'>
            <Box className='nowrap' component='span' display='flex'>
              {`${capitalize(stop.city)}, ${stop.stateCode}`}
            </Box>
            {stop.isDropTrailer && (
              <Box data-test='droptrailer' className='ml2 mt1'>
                <DropTrailerIcon size='large' />
              </Box>
            )}
          </Box>
          <Box display='flex' width='100%' alignItems='center'>
            <Box className='nowrap' component='span' display='flex'>
              {formattedStartDate}
            </Box>
            <Box className='nowrap' display='flex' ml={1}>
              <Tag size={TAG_SIZE.xsmall} tagStyle={labelStyle} label={appointmentTimeLabel} />
            </Box>
          </Box>
        </Box>
      </Box>
      <Box maxWidth={200} ml={2}>
        <ControlledDateTimeFields
          name={`${stopTypeField}.manualEta`}
          DateTimeFieldProps={{
            label: 'ETA',
            isMissing: missingFields?.manualEta,
            isDisabled: isStopCompleted,
            timezone: stop.appointment?.timezone,
            stopFieldProps: {
              ...stopFieldType,
              name: 'manualEta',
            },
          }}
        />
        {!isStopCompleted && <LateReasonDropdownRender stop={stop} stopTypeField={stopTypeField} />}
      </Box>
      <ControlledDateTimeFields
        name={`${stopTypeField}.arrivedAt`}
        DateTimeFieldProps={{
          label: 'Check In',
          isMissing: missingFields?.arrivedAt,
          timezone: stop.appointment?.timezone,
          autofillDate: !stop.arrivedAt ? stop.appointment?.startAt : undefined,
          stopFieldProps: {
            ...stopFieldType,
            name: 'arrivedAt',
          },
        }}
      />
      <ControlledDateTimeFields
        name={`${stopTypeField}.leftAt`}
        DateTimeFieldProps={{
          label: 'Check Out',
          isMissing: missingFields?.leftAt,
          timezone: stop.appointment?.timezone,
          autofillDate: !stop.leftAt
            ? stop.appointment?.endAt || stop.appointment?.startAt
            : undefined,
          stopFieldProps: {
            ...stopFieldType,
            name: 'leftAt',
          },
        }}
      />
      {isLastStop && stop.isDropTrailer && (
        <ControlledDateTimeFields
          name={`${stopTypeField}.trailerEmptiedAt`}
          DateTimeFieldProps={{
            label: 'Trailer Empty',
            isMissing: missingFields?.trailerEmptiedAt,
            timezone: stop.appointment?.timezone,
            stopFieldProps: {
              ...stopFieldType,
              name: 'trailerEmptiedAt',
            },
          }}
        />
      )}
      <ControlledHiddenInput name={`${stopTypeField}.id`} />
      {isEtaRequired && (
        <ControlledHiddenInput name={`${stopTypeField}.hasRequiredEta`} defaultValue />
      )}
    </Box>
  )
}
export default StopFields
